// Настройки уведомлений (FR-NOTIF-03/05). Durable на сервере (rest.getNotifySettings/
// updateNotifySettings — реконсиляция REST, R2-пробел про серверную персистентность ЗАКРЫТ DTO в
// shared-contracts/rest.ts). Загружаются при входе, кешируются, патчатся из SettingsScreen; читаются
// in-app-вотчером (mode/preview/sound). Это не-стор-данные → идут через transport.rest (SPEC §4.0).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { NotifySettings } from '@tele-chat/shared-contracts'
import { useRest } from '../transport/TransportContext.js'

/** Дефолт до загрузки/при ошибке — «всё, со звуком и превью». */
export const DEFAULT_NOTIFY_SETTINGS: NotifySettings = {
  mode: 'all',
  preview: true,
  sound: true,
}

export interface NotifySettingsValue {
  settings: NotifySettings
  loaded: boolean
  update(patch: Partial<NotifySettings>): Promise<void>
  reload(): Promise<void>
}

const NotifySettingsContext = createContext<NotifySettingsValue | null>(null)

export interface NotifySettingsProviderProps {
  children: ReactNode
  /** Не загружать с сервера (для тестов/незалогиненного состояния). */
  enabled?: boolean
}

export function NotifySettingsProvider({
  children,
  enabled = true,
}: NotifySettingsProviderProps): JSX.Element {
  const rest = useRest()
  const [settings, setSettings] = useState<NotifySettings>(DEFAULT_NOTIFY_SETTINGS)
  const [loaded, setLoaded] = useState(false)

  const reload = useCallback(async (): Promise<void> => {
    try {
      const s = await rest.getNotifySettings()
      setSettings(s)
    } catch {
      setSettings(DEFAULT_NOTIFY_SETTINGS)
    } finally {
      setLoaded(true)
    }
  }, [rest])

  const update = useCallback(
    async (patch: Partial<NotifySettings>): Promise<void> => {
      // Оптимистично применяем, затем подтверждаем сервером (откат при ошибке).
      const prev = settings
      const optimistic = { ...settings, ...patch }
      setSettings(optimistic)
      try {
        const confirmed = await rest.updateNotifySettings(patch)
        setSettings(confirmed)
      } catch (err) {
        setSettings(prev)
        throw err
      }
    },
    [rest, settings],
  )

  useEffect(() => {
    if (enabled) void reload()
  }, [enabled, reload])

  const value = useMemo<NotifySettingsValue>(
    () => ({ settings, loaded, update, reload }),
    [settings, loaded, update, reload],
  )

  return <NotifySettingsContext.Provider value={value}>{children}</NotifySettingsContext.Provider>
}

export function useNotifySettings(): NotifySettingsValue {
  const ctx = useContext(NotifySettingsContext)
  if (ctx === null) {
    throw new Error('useNotifySettings(): компонент должен быть внутри <NotifySettingsProvider>')
  }
  return ctx
}
